import React, { useState, useRef, Fragment, useEffect } from "react"
import JSZip from "jszip"
import { whenPropertyChanges } from "../wrappers"
import FileUpload from "./FileUpload"
import Checkbox from "./Checkbox"
import LogOverlay from "./LogOverlay"
import renderTable from "./RenderTable"
import ShowTxtFile from "./ShowTxtFile"
import {
    Column,
    MainDiv,
    Sidebar,
    Button,
    SmallButton,
    RedButton
} from "./styles"

const filterByType = (logsArray, filter) => {
    // filter is object like {octoprint: {isChecked: true, subFilters: {...}}}
    return logsArray.filter(log => filter[log.type].isChecked)
}

const filterByModule = (logsArray, filter) => {
    return logsArray.filter(log => {
        const modules = log.module.split(".")
        let currentFilter = filter
        for (let i = 0; i < modules.length; i++) {
            if (!currentFilter[modules[i]].isChecked) {
                return false
            }
            currentFilter = currentFilter[modules[i]].subFilters
        }
        return true
    })
}

const filterBoth = (logsArray, typeFilter, moduleFilter) => {
    return filterByModule(filterByType(logsArray, typeFilter), moduleFilter)
}

const searchMessage = (logsArray, searchString) => {
    return logsArray.filter(log =>
        log.message.toLowerCase().includes(searchString)
    )
}

const setSubFilters = (filters, newValue) => {
    Object.keys(filters).forEach(filterKey => {
        filters[filterKey].isChecked = newValue
        if (Object.entries(filters[filterKey].subFilters).length > 0)
            setSubFilters(filters[filterKey].subFilters, newValue)
    })
}

const parseLogLine = line => {
    const split = line.split(" - ")
    const logLine = {
        time: split[0],
        module: split[1],
        type: split[2],
        message: split[3]
    }
    return logLine
}

const splitLogFile = content => {
    let lines = []
    let newLine = content.split("\n")[0]
    content.split("\n").forEach(i => {
        // assume the lines start with the year... (valid until 2029)
        if (i.startsWith("202")) {
            lines.push(newLine)
            newLine = i
        } else {
            newLine += "\n" + i
        }
    })
    return lines
}

const RenderSizeInfo = (allData, filteredData) => {
    return (
        <div>
            <span style={{ paddingTop: "10px", paddingBottom: "10px" }}>
                {filteredData === null ? allData.length : filteredData.length}{" "}
                logs shown ({allData.length} total)
            </span>
        </div>
    )
}

const OctoprintParser = () => {
    const [files, setFiles] = useState(null)
    const [octoprintLogs, setOctoprintLogs] = useState([])
    const [status, setStatus] = useState(null)
    const [isRenderTable, setIsRenderTable] = useState(false)
    const [displayLogs, setDisplayLogs] = useState(null)
    const [typeFilter, setTypeFilter] = useState(null)
    const [moduleFilter, setModuleFilter] = useState(null)
    const [renderSingleLog, setRenderSingleLog] = useState(null)
    const [isRenderOctoprintLogs, setIsRenderOctoprintLogs] = useState(true)
    const [currentFile, setCurrentFile] = useState(null)
    const searchInput = useRef(null)

    // Watch for postMessage from CLoudDock maintenance console
    useEffect(() => {
        const handler = event => {
            console.log("Event", event)
            try {
                if (event.origin !== window.location.origin) {
                    if (event.data.unixTimestamp && event.data.zipBase64) {
                        window.localStorage.setItem(
                            "cloudDockStorage",
                            JSON.stringify(event.data)
                        )
                    }
                }
            } catch (e) {}
        }
        window.addEventListener("message", handler)
        // clean up
        return () => window.removeEventListener("message", handler)
    }, [])

    const generateFiltersFromLogs = () => {
        if (!isRenderTable) return
        if (typeFilter === null) {
            setTypeFilter(
                Array.from(new Set(octoprintLogs.map(log => log.type))).reduce(
                    (obj, key) => ({
                        ...obj,
                        [key]: { isChecked: true, subFilters: {} }
                    }),
                    {}
                )
            )
        }
        if (moduleFilter === null) {
            let modules = {}
            octoprintLogs.forEach(log => {
                const subFilters = log.module.split(".")
                subFilters.forEach((subFilter, index) => {
                    if (index === 0) {
                        if (modules[subFilter] === undefined) {
                            modules[subFilter] = {
                                isChecked: true,
                                subFilters: {}
                            }
                        }
                    } else {
                        let currentParent = modules[subFilters[0]].subFilters
                        for (let i = 1; i < index; i++) {
                            currentParent =
                                currentParent[subFilters[i]].subFilters
                        }
                        if (currentParent[subFilter] === undefined) {
                            currentParent[subFilter] = {
                                isChecked: true,
                                subFilters: {}
                            }
                        }
                    }
                })
            })
            setModuleFilter(modules)
        }
    }

    whenPropertyChanges(isRenderTable, generateFiltersFromLogs)

    const setSearchString = newValue => {
        const { current } = searchInput
        if (current) {
            current.value = newValue
            setDisplayLogs(
                searchMessage(
                    filterByModule(
                        filterByType(octoprintLogs, typeFilter),
                        moduleFilter
                    ),
                    newValue
                )
            )
        }
    }

    const getSearchString = () => {
        const { current } = searchInput
        if (current) {
            return current.value
        } else return null
    }

    const resetSearch = () => {
        setSearchString("")
        const newTypeFilter = { ...typeFilter }
        setSubFilters(newTypeFilter, true)
        setTypeFilter(newTypeFilter)
        const newModuleFilter = { ...moduleFilter }
        setSubFilters(newModuleFilter, true)
        setModuleFilter(newModuleFilter)
        setDisplayLogs(octoprintLogs)
    }

    const selectAll = (select = true) => {
        const newTypeFilter = { ...typeFilter }
        setSubFilters(newTypeFilter, select)
        setTypeFilter(newTypeFilter)
        const newModuleFilter = { ...moduleFilter }
        setSubFilters(newModuleFilter, select)
        setModuleFilter(newModuleFilter)
    }

    const renderFilters = (
        filterName,
        subFilters,
        setFilter,
        applyFilter,
        mainFilter,
        parentTree
    ) => {
        // recursively generate the filter tree (mainly for modules)
        if (!mainFilter) {
            mainFilter = subFilters
        }
        if (!parentTree) {
            parentTree = []
        }
        return (
            <div style={{ paddingLeft: "20px" }}>
                {Object.keys(subFilters).map((filter, index) => (
                    <Fragment key={index}>
                        <Checkbox
                            key={filter}
                            label={filter}
                            isSelected={subFilters[filter].isChecked}
                            onCheckboxChange={() => {
                                let newFilter = { ...mainFilter }
                                // get a reference to the correct subfilter
                                let filterToChange = newFilter
                                if (parentTree.length > 0) {
                                    parentTree.forEach(parent => {
                                        filterToChange =
                                            filterToChange[parent].subFilters
                                    })
                                }
                                // update the filter
                                filterToChange[filter] = {
                                    isChecked: !subFilters[filter].isChecked,
                                    subFilters: subFilters[filter].subFilters
                                }
                                // update subfilters to match
                                setSubFilters(
                                    filterToChange[filter].subFilters,
                                    filterToChange[filter].isChecked
                                )
                                // if checked, also check parents
                                if (
                                    filterToChange[filter].isChecked &&
                                    parentTree.length > 0
                                ) {
                                    let parent = newFilter[parentTree[0]]
                                    parent.isChecked = true
                                    parentTree
                                        .slice(1, parentTree.length)
                                        .forEach(nextParent => {
                                            parent =
                                                parent.subFilters[nextParent]
                                            parent.isChecked = true
                                        })
                                }
                                setFilter(newFilter)
                                if (filterName === "type") {
                                    setDisplayLogs(
                                        filterBoth(
                                            octoprintLogs,
                                            newFilter,
                                            moduleFilter
                                        )
                                    )
                                } else {
                                    setDisplayLogs(
                                        filterBoth(
                                            octoprintLogs,
                                            typeFilter,
                                            newFilter
                                        )
                                    )
                                }
                            }}
                        />
                        {//filter.subFilters !== {} &&
                        renderFilters(
                            filterName,
                            subFilters[filter].subFilters,
                            setFilter,
                            applyFilter,
                            mainFilter,
                            parentTree.concat(filter)
                        )}
                    </Fragment>
                ))}
            </div>
        )
    }

    const renderMessageSearch = () => {
        return (
            <div style={{ paddingLeft: "20px", paddingRight: "20px" }}>
                <input
                    style={{
                        background: "#323e50",
                        borderRadius: "3px",
                        padding: "10px"
                    }}
                    ref={searchInput}
                    onKeyUp={e => {
                        if (e.key === "Enter") {
                            setSearchString(getSearchString())
                        }
                    }}
                />{" "}
                <Button
                    onClick={() => {
                        setSearchString(getSearchString())
                    }}
                >
                    <span>Search</span>
                </Button>
            </div>
        )
    }

    const loadFromZip = e => {
        setStatus("Loading")
        const file = e.target.files[0]
        JSZip.loadAsync(file).then(async contents => {
            setFiles(contents.files)
            const promises = Object.values(contents.files)
                .sort()
                .map(
                    f =>
                        new Promise((resolve, reject) => {
                            if (f.name.includes("logs/octoprint")) {
                                try {
                                    f.async("string")
                                        .then(content => {
                                            let newLogs = splitLogFile(
                                                content
                                            ).map(line => {
                                                return parseLogLine(line)
                                            })
                                            setOctoprintLogs(octoprintLogs => [
                                                ...octoprintLogs,
                                                ...newLogs
                                            ])
                                        })
                                        .then(() => {
                                            resolve(null)
                                        })
                                } catch (e) {
                                    reject(e)
                                }
                            } else resolve(null)
                        })
                )
            await Promise.all(promises)
            setStatus(null)
            setIsRenderTable(true)
        })
    }

    // On mount, if we are targeting storage, handle appropriately
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search)
        const targetStorage = Boolean(urlParams.get("storage"))
        if (targetStorage) {
            try {
                const { zipBase64 } = JSON.parse(
                    window.localStorage.getItem("cloudDockStorage")
                )
                let arr = zipBase64.split(","),
                    mime = arr[0].match(/:(.*?);/)[1],
                    bstr = atob(arr[1]),
                    n = bstr.length,
                    u8arr = new Uint8Array(n)

                while (n--) {
                    u8arr[n] = bstr.charCodeAt(n)
                }

                const file = new File([u8arr], "logdump.zip", { type: mime })
                loadFromZip({ target: { files: [file] } })
            } catch (e) {
                console.error(e)
            }
        }
    }, [])

    const renderNetworkExplorer = () => {
        console.log("!!!", files)
        return (
            <MainDiv className="MainDiv">
                <Sidebar className="Sidebar">
                    {
                        <Button
                            onClick={() => {
                                setIsRenderOctoprintLogs(!isRenderOctoprintLogs)
                            }}
                        >
                            {"View octoprint logs"}
                        </Button>
                    }
                    <br />
                    <h1>Network logs</h1>
                    <ul>
                        {Object.keys(files)
                            .sort()
                            .map((filename, index) => {
                                if (
                                    (/logdump.*network.*/.test(filename) &&
                                        !/logdump.*network\/$/.test(
                                            filename
                                        )) ||
                                    /logdump.*buildbee\/buildbee_/.test(
                                        filename
                                    )
                                )
                                    return (
                                        <li key={index}>
                                            <SmallButton
                                                style={{ width: "100%" }}
                                                onClick={() =>
                                                    files[filename]
                                                        .async("string")
                                                        .then(content =>
                                                            setCurrentFile(
                                                                content
                                                            )
                                                        )
                                                }
                                            >
                                                {filename.split("/").slice(-1)}
                                            </SmallButton>
                                        </li>
                                    )
                                else return null
                            })}
                    </ul>
                </Sidebar>
                <Column
                    style={{ justifyContent: "flex-start", overflowX: "auto" }}
                >
                    {status && <p>{status}</p>}
                    <br />
                    {currentFile === null ? (
                        <span>Select a file</span>
                    ) : (
                        <ShowTxtFile fileContents={currentFile} />
                    )}
                </Column>
            </MainDiv>
        )
    }

    const renderLogExplorer = () => {
        if (files === null) return null
        return (
            <MainDiv className="MainDiv">
                <Sidebar className="Sidebar">
                    <Button
                        onClick={() => {
                            setIsRenderOctoprintLogs(!isRenderOctoprintLogs)
                        }}
                    >
                        {"View network logs"}
                    </Button>
                    <h2>Filter logs</h2>
                    <br />
                    <h3>Message search</h3>
                    {renderMessageSearch()}
                    {RenderSizeInfo(octoprintLogs, displayLogs)}
                    <RedButton onClick={() => resetSearch()}>
                        <span>Reset search</span>
                    </RedButton>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            width: "calc(100% - 20px)",
                            alignItems: "center"
                        }}
                    >
                        <span style={{ paddingRight: "20px" }}>Select:</span>
                        <SmallButton
                            style={{
                                marginRight: "10px"
                            }}
                            onClick={() => selectAll(true)}
                        >
                            <span>All</span>
                        </SmallButton>
                        <SmallButton onClick={() => selectAll(false)}>
                            <span>None</span>
                        </SmallButton>
                    </div>
                    <br />
                    <h3>Message types</h3>
                    {typeFilter &&
                        renderFilters(
                            "type",
                            typeFilter,
                            setTypeFilter,
                            filterByType
                        )}
                    <h3>Modules</h3>
                    {moduleFilter &&
                        renderFilters(
                            "module",
                            moduleFilter,
                            setModuleFilter,
                            filterByModule
                        )}
                    <br />
                </Sidebar>
                <Column style={{ justifyContent: "flex-start" }}>
                    {status && <p>{status}</p>}
                    <br />
                    {isRenderTable &&
                        renderTable(
                            displayLogs === null ? octoprintLogs : displayLogs,
                            setRenderSingleLog
                        )}
                </Column>
                {renderSingleLog !== null &&
                    LogOverlay(renderSingleLog, () => {
                        setRenderSingleLog(null)
                    })}
            </MainDiv>
        )
    }
    if (files === null) return FileUpload(loadFromZip)
    if (isRenderOctoprintLogs) return renderLogExplorer()
    else return renderNetworkExplorer()
}

export default OctoprintParser



// WEBPACK FOOTER //
// ./src/OctoprintParser/index.js